import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, signIn, confirmSignIn, signOut, signUp } from 'aws-amplify/auth';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (email: string) => Promise<void>;
  confirmSignIn: (code: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        await getCurrentUser();
        setIsAuthenticated(true);
      } catch {
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const signInWithEmail = async (email: string) => {
    // Prefer signIn first; only signUp when user doesn't exist.
    // This avoids unnecessary signUp side effects (e.g., extra emails) for existing users.
    try {
      await signIn({
        username: email,
        options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
      });
      return;
    } catch (e: any) {
      if (e?.name !== 'UserNotFoundException' && e?.name !== 'UserNotConfirmedException') {
        // For other errors, bubble up immediately
        throw e;
      }
    }

    // Create the user only if needed, then initiate custom auth.
    try {
      const randomPassword = generateStrongPassword();
      await signUp({
        username: email,
        password: randomPassword,
        options: { userAttributes: { email } },
      });
    } catch (e: any) {
      // If a concurrent process created the user, proceed to sign-in
      if (e?.name !== 'UsernameExistsException') {
        throw e;
      }
    }

    await signIn({
      username: email,
      options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
    });
  };

  const confirmSignInWithCode = async (code: string) => {
    await confirmSignIn({ challengeResponse: code });
    setIsAuthenticated(true);
  };

  const logout = async () => {
    await signOut();
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, signIn: signInWithEmail, confirmSignIn: confirmSignInWithCode, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function generateStrongPassword(): string {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const nums = '0123456789';
  const special = '!@#$%^&*()_+-=[]{}|;:,.<>?';
  const all = upper + lower + nums + special;
  const pick = (s: string) => s[Math.floor(Math.random() * s.length)];
  // Ensure minimum policy compliance (length >= 12 with char variety)
  const base = [pick(upper), pick(lower), pick(nums), pick(special)];
  while (base.length < 16) base.push(pick(all));
  // Shuffle
  for (let i = base.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [base[i], base[j]] = [base[j], base[i]];
  }
  return base.join('');
}
